import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const HEADER_PAD = { padding: '14px 8px', minHeight: 72 };

/** kN / kNm / N·m style: 0 decimals if |value| > 10, else 2 decimals */
function formatForceOrTorque(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const digits = Math.abs(n) > 10 ? 0 : 2;
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** S355J2 plate: typical f_u for t ≤ 40 mm (EN 10025-2); N/mm² */
const PLATE_FU_S355_MPA = 510;

const GAMMA_M2 = 1.25;

/** EN 1993-1-8 Table 3.4: maximum k₁ from spacing / edge distance (upper bound). */
const BEARING_K1_MAX = 2.5;

/**
 * Minimum gross plate thickness (mm) of S355 such that design bearing resistance
 * F_b,Rd = k₁ α_b f_u d t / γ_M2 is not below either tabulated bolt shear (threads or shank):
 * uses max(F_v,Rd,threads, F_v,Rd,shank).
 * Uses maximum code-friendly k₁ and α_b (generous edge/spacing); α_b = min(1, f_ub/f_u).
 * Rounded up to 0.5 mm.
 */
function minS355PlateThicknessForBearing(row, gradeSuffix) {
  const F_thr = Number(row[`Fv_Rd_thr${gradeSuffix}`]);
  const F_shk = Number(row[`Fv_Rd_shk${gradeSuffix}`]);
  const d = Number(row.d);
  if (!Number.isFinite(F_thr) || !Number.isFinite(F_shk) || !Number.isFinite(d) || d <= 0) return null;
  const Fv_kN = Math.max(F_thr, F_shk);
  const Fv_N = Fv_kN * 1000;
  const f_ub = gradeSuffix === '_109' ? 1000 : 800;
  const alphaB = Math.min(1, f_ub / PLATE_FU_S355_MPA);
  const denom = BEARING_K1_MAX * alphaB * PLATE_FU_S355_MPA * d;
  if (denom <= 0) return null;
  const tRaw = (Fv_N * GAMMA_M2) / denom;
  return Math.ceil(tRaw * 2) / 2;
}

function formatPlateThicknessMm(value) {
  if (value == null || !Number.isFinite(value)) return '';
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const PlainHeader = ({ children }) => (
  <div
    style={{
      whiteSpace: 'pre-line',
      lineHeight: 1.25,
      textAlign: 'center',
      ...HEADER_PAD,
    }}
  >
    {children}
  </div>
);

const TooltipHeader = ({ tooltip, children }) => (
  <Tooltip title={tooltip} arrow placement="top" enterDelay={400}>
    <div
      style={{
        whiteSpace: 'pre-line',
        lineHeight: 1.25,
        textAlign: 'center',
        cursor: 'help',
        width: '100%',
        ...HEADER_PAD,
      }}
    >
      {children}
    </div>
  </Tooltip>
);

const BoltCapacityTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [grade, setGrade] = useState('88');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/BOLTS_sections.json');
        if (!res.ok) throw new Error('Failed to load bolt data');
        const data = await res.json();
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setError('Could not load BOLTS_sections.json');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const list = !q
      ? rows
      : rows.filter(
          (r) =>
            String(r.name || '').toLowerCase().includes(q) ||
            String(r.d || '').includes(q)
        );
    return [...list].sort((a, b) => {
      const da = Number(a.d);
      const db = Number(b.d);
      if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true });
    });
  }, [rows, filterText]);

  const suffix = grade === '109' ? '_109' : '_88';

  const columns = useMemo(() => {
    const tt = {
      d: 'Nominal bolt diameter (major diameter of the thread).',
      pitch: 'Distance between thread crests along the bolt axis — coarse metric series (ISO 261).',
      A_s:
        'Tensile stress area: effective area used for tension and for shear when the shear plane cuts through the threaded part.',
      A_nom:
        'Gross area of the shank (unthreaded rod). Used for shear resistance when the shear plane lies only in the shank, clear of threads.',
      Ft_Rd:
        'Design tensile resistance of one bolt: tension capacity you may compare to axial tension in the bolt (EN 1993-1-8).',
      Fv_thr:
        'Design shear resistance when the shear plane passes through the threaded portion (threads in shear plane).',
      Fv_shk:
        'Design shear resistance when the shear plane passes only through the unthreaded shank.',
      Fp_Cd:
        'Characteristic preload used for installation: 0.7 × ultimate strength × tensile stress area. Basis for approximate tightening torque.',
      T010:
        'Indicative tightening torque using torque factor kₘ = 0.10 (T = kₘ × Fₚ,Cd × d, with Fₚ,Cd in kN and d in mm). Actual torque depends on lubrication and site practice.',
      T014:
        'Indicative tightening torque using torque factor kₘ = 0.14. Same formula as kₘ = 0.10; use the factor your procedure specifies.',
      t_S355_bearing:
        'Minimum gross thickness (mm) of S355J2 plate (f_u = 510 N/mm², t ≤ 40 mm) so that EN 1993-1-8 bearing resistance F_b,Rd = k₁ α_b f_u d t / γ_M2 ' +
        'is at least max(F_v,Rd threads, F_v,Rd shank) for the selected grade (so bearing exceeds either shear mode). γ_M2 = 1.25. ' +
        'k₁ = 2.5 (maximum from Table 3.4 for favourable edge / spacing). α_b = min(1.0, f_ub/f_u) with f_ub = 800 MPa (8.8) or 1000 MPa (10.9). ' +
        'Rounded up to the next 0.5 mm. Verify geometry, holes, packing, and National Annex.',
    };

    const sortMenuOff = { sortable: false, disableColumnMenu: true, filterable: false };

    return [
      { field: 'id', headerName: 'ID', hide: true, ...sortMenuOff },
      {
        field: 'name',
        headerName: 'Size',
        width: 76,
        ...sortMenuOff,
        renderHeader: () => <PlainHeader>Size</PlainHeader>,
      },
      {
        field: 'd',
        headerName: 'd (mm)',
        type: 'number',
        width: 68,
        ...sortMenuOff,
        renderHeader: () => (
          <TooltipHeader tooltip={tt.d}>
            {'d\n'}
            (mm)
          </TooltipHeader>
        ),
      },
      {
        field: 'pitch',
        headerName: 'Pitch P (mm)',
        type: 'number',
        width: 84,
        ...sortMenuOff,
        renderHeader: () => (
          <TooltipHeader tooltip={tt.pitch}>
            {'Pitch P\n'}
            (mm)
          </TooltipHeader>
        ),
      },
      {
        field: 'A_s',
        headerName: 'As (mm²)',
        type: 'number',
        width: 86,
        ...sortMenuOff,
        renderHeader: () => (
          <TooltipHeader tooltip={tt.A_s}>
            A<sub>s</sub>
            {'\n'}
            (mm²)
          </TooltipHeader>
        ),
      },
      {
        field: 'A_nom',
        headerName: 'A shank (mm²)',
        type: 'number',
        width: 86,
        ...sortMenuOff,
        renderHeader: () => (
          <TooltipHeader tooltip={tt.A_nom}>
            A{'\n'}
            (mm²){'\n'}shank
          </TooltipHeader>
        ),
      },
      {
        field: `Ft_Rd${suffix}`,
        headerName: 'Ft,Rd (kN)',
        type: 'number',
        width: 88,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.Ft_Rd}>
            F<sub>t,Rd</sub>
            {'\n'}
            (kN)
          </TooltipHeader>
        ),
      },
      {
        field: `Fv_Rd_thr${suffix}`,
        headerName: 'Fv,Rd threads (kN)',
        type: 'number',
        width: 94,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.Fv_thr}>
            F<sub>v,Rd</sub>
            {'\n'}
            threads{'\n'}
            (kN)
          </TooltipHeader>
        ),
      },
      {
        field: `Fv_Rd_shk${suffix}`,
        headerName: 'Fv,Rd shank (kN)',
        type: 'number',
        width: 90,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.Fv_shk}>
            F<sub>v,Rd</sub>
            {'\n'}
            shank{'\n'}
            (kN)
          </TooltipHeader>
        ),
      },
      {
        field: 't_plate_S355_bearing',
        headerName: 't plate S355 (mm)',
        type: 'number',
        width: 108,
        ...sortMenuOff,
        valueGetter: (_v, row) => minS355PlateThicknessForBearing(row, suffix),
        valueFormatter: (value) => formatPlateThicknessMm(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.t_S355_bearing}>
            t<sub>plt</sub>
            {'\n'}
            S355{'\n'}
            (mm)
          </TooltipHeader>
        ),
      },
      {
        field: `Fp_Cd${suffix}`,
        headerName: 'Fp,Cd (kN)',
        type: 'number',
        width: 88,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.Fp_Cd}>
            F<sub>p,Cd</sub>
            {'\n'}
            (kN)
          </TooltipHeader>
        ),
      },
      {
        field: `T_km010${suffix}`,
        headerName: 'T km0.10 (Nm)',
        type: 'number',
        width: 100,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.T010}>
            T{'\n'}
            k<sub>m</sub>=0.10{'\n'}
            (N·m)
          </TooltipHeader>
        ),
      },
      {
        field: `T_km014${suffix}`,
        headerName: 'T km0.14 (Nm)',
        type: 'number',
        width: 100,
        ...sortMenuOff,
        valueFormatter: (value) => formatForceOrTorque(value),
        renderHeader: () => (
          <TooltipHeader tooltip={tt.T014}>
            T{'\n'}
            k<sub>m</sub>=0.14{'\n'}
            (N·m)
          </TooltipHeader>
        ),
      },
    ];
  }, [suffix]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 960 }}>
        Metric coarse thread (ISO 261). Aₛ and shank area A from ISO 898-1 stress-area formula; capacities per EN
        1993-1-8 with γ<sub>M2</sub> = 1.25. Use the toggle for bolt grade: f<sub>ub</sub> = 800 MPa (8.8) or 1000 MPa
        (10.9). Threads in shear plane: α<sub>v</sub> = 0.6 (8.8) and 0.5 (10.9). Shank in shear plane: α
        <sub>v</sub> = 0.6. F<sub>p,Cd</sub> = 0.7 f<sub>ub</sub> Aₛ. Torque T = k<sub>m</sub> F<sub>p,Cd</sub> d with
        F<sub>p,Cd</sub> in kN and d in mm → T in N·m. Values &gt; 10 (kN or N·m) are shown with 0 decimals. Column t<sub>plt</sub> S355 is the minimum plate
        thickness (0.5 mm steps) for bearing on S355J2 (f<sub>u</sub> = 510 N/mm²) to exceed max(F<sub>v,Rd</sub> threads, shank) using EN 1993-1-8 Table 3.4 at k<sub>1</sub> = 2.5
        and α<sub>b</sub> = min(1, f<sub>ub</sub>/f<sub>u</sub>). Hover column titles for short explanations.
      </Typography>

      <Box mb={2} display="flex" flexWrap="wrap" alignItems="center" gap={2}>
        <TextField
          label="Filter by size"
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary" component="span">
            Grade
          </Typography>
          <ToggleButtonGroup
            value={grade}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (v != null) setGrade(v);
            }}
            aria-label="Bolt steel grade"
          >
            <ToggleButton value="88" aria-label="8.8">
              8.8
            </ToggleButton>
            <ToggleButton value="109" aria-label="10.9">
              10.9
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            columnHeaderHeight={112}
            getRowId={(r) => r.id}
            checkboxSelection={false}
            disableRowSelectionOnClick
            disableColumnSort
            disableColumnMenu
            hideSortIcons
            disableColumnFilter
            density="compact"
            initialState={{
              columns: { columnVisibilityModel: { id: false } },
            }}
            sx={{
              minWidth: 720,
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-columnHeader': {
                alignItems: 'stretch',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-menuIcon': { display: 'none' },
              '& .MuiDataGrid-sortIcon': { display: 'none' },
              '& .MuiDataGrid-columnHeader .MuiIconButton-root': { display: 'none' },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default BoltCapacityTable;
